import React, { useState, useEffect, useMemo } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Tooltip,
  Typography,
  Chip,
  TextField,
  InputAdornment,
} from "@mui/material";
import PrintIcon from "@mui/icons-material/Print";
import SearchIcon from "@mui/icons-material/Search";
import EnhancedTable from "components/common/EnhancedTable";
import { useSessionClass } from "contexts/SessionClassContext";
import { useAuth } from "hooks/useAuth";
import { useError } from "contexts/ErrorContext";
import { useStudents } from "hooks/useStudents";
import {
  listCertificateTemplatesFromApi,
  CustomCertificateTemplate,
} from "services/certificateTemplateApiService";

export default function CertificateManagementPage() {
  const { selectedClass, selectedSession } = useSessionClass();
  const { user } = useAuth();
  const { showError, showWarning } = useError();
  const schoolId = user?.schoolId;
  const [searchTerm, setSearchTerm] = useState("");

  const classId = selectedClass?.id ?? null;
  const className = selectedClass?.name ?? "";
  const sessionName = selectedSession?.name ?? "";
  const sessionId = selectedSession?.id ?? null;

  // ── Query Students ────────────────────────────────────────────────────────
  const { useStudentsBySessionIdAndClassId } = useStudents(false, schoolId || undefined);
  const studentsQuery = useStudentsBySessionIdAndClassId(sessionId ?? undefined, classId ?? undefined);

  const studentsList = useMemo(() => {
    return studentsQuery.data ?? [];
  }, [studentsQuery.data]);

  // ── Fetch Certificate Templates ──────────────────────────────────────────
  const [templates, setTemplates] = useState<CustomCertificateTemplate[]>([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState<number | "">("");
  const [templatesLoading, setTemplatesLoading] = useState(false);

  useEffect(() => {
    const fetchTemplates = async () => {
      if (!className || !sessionId) {
        setTemplates([]);
        setSelectedTemplateId("");
        return;
      }
      setTemplatesLoading(true);
      try {
        const list = await listCertificateTemplatesFromApi(className, sessionId);
        setTemplates(list);
        
        // Auto-select primary default template if available
        const primary = list.find((t) => t.primary);
        if (primary && primary.id) {
          setSelectedTemplateId(primary.id);
        } else if (list.length > 0 && list[0].id) {
          setSelectedTemplateId(list[0].id);
        } else {
          setSelectedTemplateId("");
        }
      } catch (err) {
        console.error("Failed to load certificate layouts:", err);
      } finally {
        setTemplatesLoading(false);
      }
    };

    fetchTemplates();
  }, [className, sessionId]);

  const activeTemplate = useMemo(() => {
    return templates.find((t) => t.id === selectedTemplateId) ?? null;
  }, [templates, selectedTemplateId]);

  // ── Generate Individual Certificate ───────────────────────────────────────
  const handleGenerateCertificate = (student: any) => {
    if (!activeTemplate) {
      showWarning("Please select a Certificate Template first.");
      return;
    }

    const { svgContent } = activeTemplate;
    if (!svgContent) {
      showError("Selected template has no print layout. Edit the design first.");
      return;
    }

    const studentNameStr = (student.studentName || `${student.firstName || ""} ${student.lastName || ""}`.trim()) || "Unknown Student";
    const rollNoStr = student.rollNumber || "—";
    const fatherNameStr = student.fatherName || "—";
    
    // Formatting Date of Birth nicely
    let dobStr = "—";
    if (student.dateOfBirth) {
      if (Array.isArray(student.dateOfBirth) && student.dateOfBirth.length >= 3) {
        const [year, month, day] = student.dateOfBirth;
        dobStr = `${String(day).padStart(2, "0")}/${String(month).padStart(2, "0")}/${year}`;
      } else if (typeof student.dateOfBirth === "string") {
        dobStr = student.dateOfBirth.split("-").reverse().join("/");
      }
    }

    // Dynamic replacement in scalable vector graphics
    const filledSvg = svgContent
      .replace(/\{\{student_name\}\}/g, studentNameStr)
      .replace(/\{\{roll_no\}\}/g, rollNoStr)
      .replace(/\{\{class_name\}\}/g, className)
      .replace(/\{\{session_name\}\}/g, sessionName)
      .replace(/\{\{father_name\}\}/g, fatherNameStr)
      .replace(/\{\{dob\}\}/g, dobStr)
      .replace(/\{\{issue_date\}\}/g, new Date().toLocaleDateString());

    // Launch standalone crisp vector print view in new tab
    const printTab = window.open("", "_blank");
    if (printTab) {
      printTab.document.write(`
        <html>
          <head>
            <title>${studentNameStr} - Certificate</title>
            <style>
              body {
                margin: 0;
                display: flex;
                align-items: center;
                justify-content: center;
                height: 100vh;
                background-color: #f8fafc;
              }
              svg {
                width: 100%;
                max-width: 842px;
                height: auto;
                box-shadow: 0 15px 40px rgba(0,0,0,0.12);
                background-color: #ffffff;
              }
              @media print {
                body { background-color: #ffffff; }
                svg { box-shadow: none; max-width: 100%; width: 100%; height: 100%; }
              }
            </style>
          </head>
          <body>
            ${filledSvg}
            <script>
              window.onload = function() {
                setTimeout(function() { window.print(); }, 500);
              }
            </script>
          </body>
        </html>
      `);
      printTab.document.close();
    }
  };

  return (
    <Paper sx={{ p: 1.5, maxWidth: 1500, mx: "auto", mt: 1 }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1 }}>
        <Typography variant="h6" fontWeight={700} sx={{ fontSize: "1.25rem", whiteSpace: "nowrap" }}>
          Certificate Management
        </Typography>

        <Box sx={{ display: "flex", gap: 1, alignItems: "center", flexWrap: "wrap" }}>
          {classId && sessionId && (
            <TextField
              size="small"
              placeholder="Search..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" sx={{ color: "rgba(0,0,0,0.6)" }} />
                  </InputAdornment>
                ),
              }}
              sx={{ width: 180, '& .MuiInputBase-root': { height: 36 } }}
            />
          )}

          {/* Template Selection Dropdown */}
          <Box sx={{ minWidth: 220 }}>
            {templatesLoading ? (
              <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                <CircularProgress size={16} />
                <Typography variant="body2" color="text.secondary">Loading designs...</Typography>
              </Box>
            ) : className && templates.length > 0 ? (
              <FormControl size="small" fullWidth>
                <InputLabel>Active Layout Template</InputLabel>
                <Select
                  value={selectedTemplateId}
                  label="Active Layout Template"
                  onChange={(e) => setSelectedTemplateId(Number(e.target.value))}
                >
                  {templates.map((t) => (
                    <MenuItem key={t.id} value={t.id}>
                      {t.templateName} {t.primary && "(Default)"}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            ) : className ? (
              <Chip label="No Layouts Found" color="warning" variant="outlined" size="small" />
            ) : null}
          </Box>
        </Box>
      </Box>

      {(!classId || !sessionId) && (
        <Alert severity="info">
          Please select a Class and Session from the header dropdown to view students and layouts.
        </Alert>
      )}

      {classId && sessionId && (
        <>
          {templates.length === 0 ? (
            <Alert severity="warning" sx={{ mb: 1 }}>
              There are no active design layouts created for <strong>{className}</strong>. Go to the <strong>Certificate Template Builder</strong> tab first to create one!
            </Alert>
          ) : !selectedTemplateId ? (
            <Alert severity="info" sx={{ mb: 1 }}>
              Please select a layout design template from the dropdown above.
            </Alert>
          ) : null}

          <EnhancedTable
            columns={[
              { id: "rollNumber", label: "Roll No.", minWidth: 100 },
              { id: "studentName", label: "Student Name", minWidth: 180, format: (_, row: any) => row.studentName || `${row.firstName || ""} ${row.lastName || ""}`.trim() || "—" },
              { id: "scholarNo", label: "Scholar No.", minWidth: 120 },
              { id: "fatherName", label: "Father Name", minWidth: 160 },
              { id: "gender", label: "Gender", minWidth: 100 },
            ]}
            data={studentsList}
            loading={studentsQuery.isLoading}
            searchFields={["studentName", "firstName", "lastName", "rollNumber", "scholarNo"]}
            hideSearch
            externalSearchTerm={searchTerm}
            actions={(row: any) => (
              <Tooltip title="Generate & Print Certificate">
                <span>
                  <Button
                    variant="contained"
                    size="small"
                    startIcon={<PrintIcon />}
                    onClick={() => handleGenerateCertificate(row)}
                    disabled={!selectedTemplateId}
                    sx={{ backgroundColor: "#3092e3", "&:hover": { backgroundColor: "primary.main" }, height: 32 }}
                  >
                    Print
                  </Button>
                </span>
              </Tooltip>
            )}
            cardProps={{
              elevation: 0,
              sx: {
                border: "none",
                boxShadow: "none",
                backgroundColor: "transparent",
                mb: 0,
                '& .MuiCardContent-root': { p: 0, '&:last-child': { pb: 0 } },
              }
            }}
          />
        </>
      )}
    </Paper>
  );
}
